import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Fingerprint, KeyRound } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Brandmark } from '@/components/layout/Brandmark';
import { useAuthStore } from '@/stores/authStore';
import { useLockStore } from '@/stores/lockStore';
import { useLogout } from '@/hooks/useAuthMutations';
import { verifyPasskey } from '@/lib/webauthn';
import { haptic } from '@/lib/audio';

export function LockScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const credentialId = useLockStore((s) => s.credentialId);
  const unlock = useLockStore((s) => s.unlock);
  const logout = useLogout();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (typeof document === 'undefined') return null;
  if (!credentialId) return null;

  const handleUnlock = async () => {
    setError(null);
    setPending(true);
    try {
      const ok = await verifyPasskey(credentialId);
      if (ok) {
        haptic('success');
        unlock();
      } else {
        haptic('error');
        setError(t('lock.failed'));
      }
    } catch {
      haptic('error');
      setError(t('lock.failed'));
    } finally {
      setPending(false);
    }
  };

  const handleFallback = () => {
    // Drop the session and let the cashier re-login with password.
    logout.mutate(undefined, {
      onSettled: () => {
        navigate('/login', { replace: true });
      },
    });
  };

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-background/95 p-6 backdrop-blur-md">
      <div className="w-full max-w-sm space-y-6 rounded-lg border border-border bg-card p-8 shadow-ticket-hover">
        <div className="flex flex-col items-center gap-3 text-center">
          <Brandmark className="text-primary" size={40} />
          <div>
            <div className="label-tracked">{t('lock.heading')}</div>
            {user && (
              <h2 className="mt-1 font-mono text-2xl font-semibold leading-tight text-foreground">
                {user.name}
              </h2>
            )}
          </div>
        </div>

        <div className="flex flex-col items-center gap-3 pt-2">
          <button
            type="button"
            onClick={handleUnlock}
            disabled={pending}
            className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-primary/40 bg-primary/5 text-primary transition-colors hover:border-primary hover:bg-primary/10 disabled:opacity-60"
            aria-label={t('lock.unlock')}
          >
            <Fingerprint className="h-10 w-10" strokeWidth={1.8} />
          </button>
          <p className="text-sm text-muted-foreground">
            {pending ? t('lock.verifying') : t('lock.tapToUnlock')}
          </p>
          {error && <p className="text-sm font-medium text-destructive">{error}</p>}
        </div>

        <div className="dotted-rule" />

        <Button
          variant="ghost"
          size="lg"
          className="w-full"
          onClick={handleFallback}
          disabled={logout.isPending}
        >
          <KeyRound className="mr-2 h-4 w-4" />
          {t('lock.usePassword')}
        </Button>
      </div>
    </div>,
    document.body,
  );
}
